import type {
  DomainRouterConnection,
  DomainRouterJsonInput,
} from "@triedge/domain-core";
import type {
  AcceptFinancialAidAwardInput,
  FinancialAidAppeal,
  FinancialAidAppealHistoryResponse,
  FinancialAidAppealListResponse,
  FinancialAidAppealSummary,
  FinancialAidApplication,
  FinancialAidApplicationReportArtifactList,
  FinancialAidApplicationReportDownloadUrl,
  FinancialAidApplicationReportGenerationResult,
  FinancialAidDocumentChecklistResponse,
  FinancialAidDocumentStatus,
  FinancialAidDocumentUploadResponse,
  FinancialAidFamilyLookupResponse,
  FinancialAidInviteLinkResult,
  FinancialAidLetterResult,
  FinancialAidOpenCycle,
  FinancialAidParentStatusView,
  FinancialAidReport,
  FinancialAidReportExport,
  FinancialAidReviewDetail,
  FinancialAidReviewListItem,
  FinancialAidReviewListResponse,
  FinancialAidStatusHistoryResponse,
  MakeFinancialAidDecisionInput,
  ReviewFlag,
  CreateFinancialAidApplicationInput,
  SubmitFinancialAidApplicationInput,
  UpdateFinancialAidApplicationInput,
  InviteFinancialAidApplicationInput,
  CopyFinancialAidInviteLinkInput,
  ResendFinancialAidInviteInput,
  ClaimFinancialAidInviteInput,
  QaFinancialAidBaselineResetApplyResult,
  QaFinancialAidBaselineResetPreview,
  QaFinancialAidBaselineResetRollbackResult,
} from "./index";

export type FinancialAidApplicationConnection =
  DomainRouterConnection<FinancialAidApplication>;
export type FinancialAidReviewConnection =
  DomainRouterConnection<FinancialAidReviewListItem>;
export type FinancialAidInviteEmailQueueState = {
  emailQueued?: boolean;
  emailQueueReason?: string | null;
  emailQueueReasonCode?: string | null;
  emailQueueMessage?: string | null;
};
export type FinancialAidInviteApplicationResult = FinancialAidApplication &
  FinancialAidInviteEmailQueueState;

export const FINANCIAL_AID_QUERY_OPERATION_IDS = [
  "getFinancialAidDocumentStatus",
  "getFinancialAidApplication",
  "getFinancialAidByApplicant",
  "listFinancialAidApplicationsForReview",
  "getFinancialAidApplicationForReview",
  "getFinancialAidStatusHistory",
  "getFinancialAidParentStatusView",
  "generateFinancialAidReport",
  "listFinancialAidApplicationReportArtifacts",
  "listPendingFinancialAidAppeals",
  "getFinancialAidAppealSummary",
  "getFinancialAidAppealHistory",
  "getFinancialAidOpenCycle",
  "lookupFinancialAidFamily",
  "previewQaFinancialAidBaselineReset",
] as const;

export const FINANCIAL_AID_MUTATION_OPERATION_IDS = [
  "createFinancialAidApplication",
  "updateFinancialAidApplication",
  "submitFinancialAidApplication",
  "uploadFinancialAidDocument",
  "confirmFinancialAidDocumentUpload",
  "beginFinancialAidReview",
  "makeFinancialAidDecision",
  "assignFinancialAidApplication",
  "resolveFinancialAidFlag",
  "requestFinancialAidMoreInfo",
  "acceptFinancialAidAward",
  "declineFinancialAidAward",
  "respondToFinancialAidInfoRequest",
  "generateFinancialAidDecisionLetter",
  "generateFinancialAidApplicationReport",
  "getFinancialAidApplicationReportDownloadUrl",
  "exportFinancialAidReport",
  "createFinancialAidAppeal",
  "makeFinancialAidAppealDecision",
  "updateFinancialAidDocumentChecklist",
  "inviteFinancialAidApplication",
  "copyFinancialAidInviteLink",
  "resendFinancialAidInvite",
  "recordFinancialAidInviteOpen",
  "claimFinancialAidInvite",
  "applyQaFinancialAidBaselineReset",
  "rollbackQaFinancialAidBaselineReset",
] as const;

export type FinancialAidQueryOperationId =
  (typeof FINANCIAL_AID_QUERY_OPERATION_IDS)[number];
export type FinancialAidMutationOperationId =
  (typeof FINANCIAL_AID_MUTATION_OPERATION_IDS)[number];

export type FinancialAidQueryArgsMap = {
  getFinancialAidDocumentStatus: { applicationId: string };
  getFinancialAidApplication: { applicationId: string };
  getFinancialAidByApplicant: {
    applicantId: string;
    academicYear?: string | null;
    limit?: number;
    nextToken?: string | null;
  };
  listFinancialAidApplicationsForReview: { input?: DomainRouterJsonInput };
  getFinancialAidApplicationForReview: { applicationId: string };
  getFinancialAidStatusHistory: { applicationId: string };
  getFinancialAidParentStatusView: { applicationId: string };
  generateFinancialAidReport: { input: DomainRouterJsonInput };
  listFinancialAidApplicationReportArtifacts: { input: DomainRouterJsonInput };
  listPendingFinancialAidAppeals: { input?: DomainRouterJsonInput };
  getFinancialAidAppealSummary: { appealId: string };
  getFinancialAidAppealHistory: { applicationId: string };
  getFinancialAidOpenCycle: { academicYear?: string };
  lookupFinancialAidFamily: {
    email?: string;
    lastName?: string;
    familyName?: string;
    academicYear?: string;
    limit?: number;
  };
  previewQaFinancialAidBaselineReset: { input: DomainRouterJsonInput };
};

export type FinancialAidQueryResultMap = {
  getFinancialAidDocumentStatus: FinancialAidDocumentStatus;
  getFinancialAidApplication: FinancialAidApplication;
  getFinancialAidByApplicant: FinancialAidApplicationConnection;
  listFinancialAidApplicationsForReview:
    | FinancialAidReviewListResponse
    | FinancialAidReviewConnection;
  getFinancialAidApplicationForReview: FinancialAidReviewDetail;
  getFinancialAidStatusHistory: FinancialAidStatusHistoryResponse;
  getFinancialAidParentStatusView: FinancialAidParentStatusView;
  generateFinancialAidReport: FinancialAidReport;
  listFinancialAidApplicationReportArtifacts: FinancialAidApplicationReportArtifactList;
  listPendingFinancialAidAppeals: FinancialAidAppealListResponse;
  getFinancialAidAppealSummary: FinancialAidAppealSummary;
  getFinancialAidAppealHistory: FinancialAidAppealHistoryResponse;
  getFinancialAidOpenCycle: FinancialAidOpenCycle;
  lookupFinancialAidFamily: FinancialAidFamilyLookupResponse;
  previewQaFinancialAidBaselineReset: QaFinancialAidBaselineResetPreview;
};

export type FinancialAidMutationArgsMap = {
  createFinancialAidApplication: {
    input: CreateFinancialAidApplicationInput | DomainRouterJsonInput;
  };
  updateFinancialAidApplication: {
    input: UpdateFinancialAidApplicationInput | DomainRouterJsonInput;
  };
  submitFinancialAidApplication: {
    input: SubmitFinancialAidApplicationInput | DomainRouterJsonInput;
  };
  uploadFinancialAidDocument: { input: DomainRouterJsonInput };
  confirmFinancialAidDocumentUpload: { input: DomainRouterJsonInput };
  beginFinancialAidReview: { input: DomainRouterJsonInput };
  makeFinancialAidDecision: {
    input: MakeFinancialAidDecisionInput | DomainRouterJsonInput;
  };
  assignFinancialAidApplication: { input: DomainRouterJsonInput };
  resolveFinancialAidFlag: { input: DomainRouterJsonInput };
  requestFinancialAidMoreInfo: { input: DomainRouterJsonInput };
  acceptFinancialAidAward: {
    input: AcceptFinancialAidAwardInput | DomainRouterJsonInput;
  };
  declineFinancialAidAward: { input: DomainRouterJsonInput };
  respondToFinancialAidInfoRequest: { input: DomainRouterJsonInput };
  generateFinancialAidDecisionLetter: { input: DomainRouterJsonInput };
  generateFinancialAidApplicationReport: { input: DomainRouterJsonInput };
  getFinancialAidApplicationReportDownloadUrl: { input: DomainRouterJsonInput };
  exportFinancialAidReport: { input: DomainRouterJsonInput };
  createFinancialAidAppeal: { input: DomainRouterJsonInput };
  makeFinancialAidAppealDecision: { input: DomainRouterJsonInput };
  updateFinancialAidDocumentChecklist: { input: DomainRouterJsonInput };
  inviteFinancialAidApplication: {
    input: InviteFinancialAidApplicationInput | DomainRouterJsonInput;
  };
  copyFinancialAidInviteLink: {
    input: CopyFinancialAidInviteLinkInput | DomainRouterJsonInput;
  };
  resendFinancialAidInvite: {
    input: ResendFinancialAidInviteInput | DomainRouterJsonInput;
  };
  recordFinancialAidInviteOpen: {
    input: { token: string } | DomainRouterJsonInput;
  };
  claimFinancialAidInvite: {
    input: ClaimFinancialAidInviteInput | DomainRouterJsonInput;
  };
  applyQaFinancialAidBaselineReset: { input: DomainRouterJsonInput };
  rollbackQaFinancialAidBaselineReset: { input: DomainRouterJsonInput };
};

export type FinancialAidMutationResultMap = {
  createFinancialAidApplication: FinancialAidApplication;
  updateFinancialAidApplication: FinancialAidApplication;
  submitFinancialAidApplication: FinancialAidApplication;
  uploadFinancialAidDocument: FinancialAidDocumentUploadResponse;
  confirmFinancialAidDocumentUpload: FinancialAidDocumentChecklistResponse;
  beginFinancialAidReview: FinancialAidApplication;
  makeFinancialAidDecision: FinancialAidApplication;
  assignFinancialAidApplication: FinancialAidApplication;
  resolveFinancialAidFlag: ReviewFlag;
  requestFinancialAidMoreInfo: FinancialAidApplication;
  acceptFinancialAidAward: FinancialAidApplication;
  declineFinancialAidAward: FinancialAidApplication;
  respondToFinancialAidInfoRequest: FinancialAidApplication;
  generateFinancialAidDecisionLetter: FinancialAidLetterResult;
  generateFinancialAidApplicationReport: FinancialAidApplicationReportGenerationResult;
  getFinancialAidApplicationReportDownloadUrl: FinancialAidApplicationReportDownloadUrl;
  exportFinancialAidReport: FinancialAidReportExport;
  createFinancialAidAppeal: FinancialAidAppeal;
  makeFinancialAidAppealDecision: FinancialAidAppeal;
  updateFinancialAidDocumentChecklist: FinancialAidDocumentChecklistResponse;
  inviteFinancialAidApplication: FinancialAidInviteApplicationResult;
  copyFinancialAidInviteLink: FinancialAidInviteLinkResult;
  resendFinancialAidInvite: FinancialAidInviteApplicationResult;
  recordFinancialAidInviteOpen: FinancialAidApplication;
  claimFinancialAidInvite: FinancialAidApplication;
  applyQaFinancialAidBaselineReset: QaFinancialAidBaselineResetApplyResult;
  rollbackQaFinancialAidBaselineReset: QaFinancialAidBaselineResetRollbackResult;
};
